import React, { useState, useEffect } from 'react'
import styled from 'styled-components'
import classnames from 'clsx'
import CloseButton from 'components/Common/CloseButton'
import BinocularsIcon from 'components/Luxkit/Icons/custom/BinocularsIcon'
import FlexibleBookingsIcon from 'components/Luxkit/Icons/custom/FlexibleBookingsIcon'
import { rem } from 'polished'
import zIndex from 'styles/tools/z-index'

import { useAppDispatch } from 'hooks/reduxHooks'
import { closeSocialWidget } from 'actions/UiActions'
import BodyText from 'components/Luxkit/Typography/BodyText'

interface Props {
  totalPurchases?: number;
  pageViews?: number;
  showPageViews?: boolean;
  showTotalPurchases?: boolean;
}

const Root = styled.div`
  width: 100%;
  position: fixed;
  z-index: ${zIndex.stickySocialWidget};
  bottom: 0;
  transform: translate(-50%, -50%);
  left: 50%;
  overflow-y: hidden;
  max-height: ${rem(100)};
  transition: all .5s;

  &.closed-widget {
    max-height: 0;
  }
`

const WidgetWrapper = styled.div`
  display: flex;
  position: relative;
  align-items: center;
  width: ${rem(368)};
  height: ${rem(69)};
  border-radius: ${props => props.theme.borderRadius.medium};
  margin: 0 auto;
  background-color: ${props => props.theme.palette.neutral.eight};
  box-shadow: 0 2px 4px 0 ${({ theme }) => theme.palette.neutral.five};
  border: solid 1px ${({ theme }) => theme.palette.neutral.five};
`
const StyledCloseButton = styled(CloseButton)`
  position: absolute;
  top: ${rem(16)};
  right: ${rem(16)};
`

const StyledBinocularsIcon = styled(BinocularsIcon)`
  width: ${rem(42)};
  height: ${rem(22)};
  margin-left: ${rem(4)};
  margin-right: ${rem(4)};
`
const StyledFlexibleBookingsIcon = styled(FlexibleBookingsIcon)`
  width: ${rem(58)};
  height: ${rem(42)};
`

const Content = styled.div`
  margin-left: ${rem(4)};
`
const IconWrapper = styled.div`
  margin-top: ${rem(10)};
  margin-left: ${rem(4)};
`

function SocialProofWidget(props: Props) {
  const dispatch = useAppDispatch()

  const [invisible, setInvisible] = useState(true)

  // show the widget after 1 sec
  useEffect(
    () => {
      const timer = setTimeout(() => setInvisible(false), 1000)
      return () => {
        clearTimeout(timer)
      }
    },
    [],
  )

  // hide the widget after 10 sec
  useEffect(
    () => {
      const timer = setTimeout(() => setInvisible(true), 10000)
      return () => {
        clearTimeout(timer)
      }
    },
    [],
  )

  const { pageViews, totalPurchases, showPageViews, showTotalPurchases } = props

  // case1:  show page views if pageviews > 200 and its enabled
  if (showPageViews) {
    return (
      <Root className={classnames({ 'closed-widget': invisible })}>
        <WidgetWrapper >
          <IconWrapper>
            <StyledBinocularsIcon colour="primary"/>
          </IconWrapper>
          <Content>
            <BodyText variant="medium" align="center">This escape was viewed <strong> {Number(pageViews).toLocaleString('en-AU')} times</strong></BodyText>
            <BodyText variant="medium" align="start">over the last <strong>24 hours</strong></BodyText>
          </Content>
          <StyledCloseButton onClick={() => dispatch(closeSocialWidget())} />
        </WidgetWrapper>
      </Root>
    )
  }
  // case2:  show total purchases if totalPurchase > 5 and its enabled
  if (showTotalPurchases) {
    return (
      <Root className={classnames({ 'closed-widget': invisible })}>
        <WidgetWrapper >
          <IconWrapper>
            <StyledFlexibleBookingsIcon colour="primary"/>
          </IconWrapper>
          <Content>
            <BodyText variant="medium" align="center"><strong>{Number(totalPurchases).toLocaleString('en-AU')} people</strong> have booked this escape</BodyText>
            <BodyText variant="medium" align="start">over the last <strong>24 hours</strong></BodyText>
          </Content>
          <StyledCloseButton onClick={() => dispatch(closeSocialWidget())} />
        </WidgetWrapper>
      </Root>
    )
  }
}

export default SocialProofWidget
